import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
const milestones = [
  { year: '2024 Q1', title: '平台成立', desc: '鲍鱼tv正式上线运营，首批上线5000+影视资源' },
  { year: '2024 Q2', title: '百万用户', desc: '用户量突破100万，推出移动端App' },
  { year: '2024 Q3', title: '4K画质', desc: '全面支持4K超清画质播放，行业领先' },
  { year: '2024 Q4', title: '千万用户', desc: '累计用户突破1000万，上线智能电视版' },
  { year: '2025 Q1', title: '直播上线', desc: '新增直播频道，覆盖电竞赛事、综艺直播' },
  { year: '2025 Q2', title: '亿级用户', desc: '累计用户突破1亿，成为行业标杆平台' },
  { year: '2025 Q4', title: 'AI赋能', desc: '上线AI智能推荐系统，个性化体验再升级' },
  { year: '2026', title: '5亿用户', desc: '累计用户突破5亿，持续引领行业发展' },
];

const testimonials = [
  {
    name: '张先生',
    role: '资深影迷',
    content: '鲍鱼tv的画质真的太棒了！4K超清看大片完全就是影院级体验，而且资源更新速度很快，新上映的电影很快就能看到。推荐给所有喜欢看电影的朋友！',
    rating: 5,
  },
  {
    name: '李女士',
    role: '追剧达人',
    content: '作为一个追剧狂魔，鲍鱼tv简直是我的救星！综艺、电视剧分类清晰，收藏和历史记录功能特别方便，手机和电视无缝切换，太贴心了。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '电竞爱好者',
    content: '看LPL比赛只认鲍鱼tv！直播延迟超低，弹幕氛围好，还有实时数据面板。画面清晰度比其他平台高出一截，团战细节一览无余。',
    rating: 5,
  },
  {
    name: '赵女士',
    role: '家庭用户',
    content: '家里老人和孩子都在用鲍鱼tv，内容覆盖面广，从动画片到纪录片应有尽有。儿童模式的设计很贴心，不用担心孩子看到不适合的内容。',
    rating: 4,
  },
];

const faqs = [
  {
    q: '鲍鱼tv的公司背景是什么？',
    a: '鲍鱼tv由国内顶尖互联网技术团队创立，总部位于北京，是一家专注于高清视频在线播放的科技公司。公司拥有自主研发的视频编解码技术和智能推荐算法，致力于为用户提供极致的观影体验。',
  },
  {
    q: '鲍鱼tv与其他视频平台有什么区别？',
    a: '鲍鱼tv的核心优势在于：行业领先的4K超清画质、低于1秒的超低直播延迟、海量正版内容资源、AI智能推荐系统、全终端无缝同步体验。我们始终坚持技术驱动内容，为用户打造最佳观影平台。',
  },
];

export default function About() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q4hisnlsngrm4llrc0', {
        method: 'POST',
        body: new URLSearchParams(
          Object.fromEntries(payload.entries()) as Record<string, string>
        ).toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const errMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          (responseText.includes('spam') ? '提交被拦截，请稍后再试' : '提交失败，请稍后再试');
        setFormError(errMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '鲍鱼tv',
    url: 'https://www.baoyutv.com/',
    description: '高清视频在线观看平台',
    foundingDate: '2024-01-01',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      telephone: '+86-400-888-0000',
      email: 'service@baoyutv.com',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqLd)}</script>

      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Hero */}
          <section className="w-full py-16 bg-background-100">
            <div className="max-w-4xl mx-auto text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于鲍鱼tv
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                鲍鱼tv成立于2024年，是国内领先的高清视频在线观看平台。
                我们致力于通过技术创新，为全球华语用户提供丰富、高清、流畅的视频娱乐体验。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full py-14 bg-background-50">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-600 leading-loose mb-4">
                    鲍鱼tv的创始团队来自国内顶尖互联网公司，怀揣着"让每个人都能轻松享受高品质视频内容"的初心，于2024年正式创立了鲍鱼tv平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-loose mb-4">
                    从最初的5000部影片起步，到如今拥有超过10万部影视资源的内容矩阵，鲍鱼tv用两年多的时间，完成了从创业公司到行业标杆的跨越。我们自主研发的视频编解码技术、AI智能推荐算法，以及遍布全国的CDN加速节点，共同构筑了鲍鱼tv的核心竞争力。
                  </p>
                  <p className="text-sm text-foreground-600 leading-loose">
                    未来，鲍鱼tv将继续深耕视频技术领域，拓展更多优质内容，为全球用户带来更加极致的观影体验。
                  </p>
                </div>
                <div className="aspect-video rounded-lg overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20with%20team%20collaboration%2C%20creative%20workspace%2C%20large%20screens%20displaying%20video%20content%2C%20warm%20orange%20and%20amber%20lighting%2C%20clean%20minimalist%20design%2C%20professional%20atmosphere&width=800&height=450&seq=baoyu-about-1&orientation=landscape"
                    alt="鲍鱼tv团队"
                    title="鲍鱼tv - 专业视频技术团队"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-secondary-300 hidden md:block"></div>
                <div className="space-y-6">
                  {milestones.map((m, i) => (
                    <div
                      key={m.year}
                      className={`relative flex items-center gap-6 ${
                        i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="flex-1 hidden md:block"></div>
                      <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 relative z-10 hidden md:block"></div>
                      <div className="flex-1 bg-background-50 rounded-lg p-5 border border-background-200/70 md:border-none md:p-0 md:bg-transparent">
                        <span className="inline-block px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full font-medium mb-2">
                          {m.year}
                        </span>
                        <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-500">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full py-14 bg-background-50">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <i
                          key={idx}
                          className={`ri-star-fill text-sm ${
                            idx < t.rating ? 'text-accent-500' : 'text-background-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact + FAQ */}
          <section className="w-full py-14 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Contact Form */}
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-6">
                    有任何问题或建议？请填写以下表单，我们会尽快回复您。
                  </p>

                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) =>
                          setFormData({ ...formData, subject: e.target.value })
                        }
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setFormData({ ...formData, message: e.target.value });
                          }
                        }}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">
                        {formData.message.length}/500
                      </p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-700 text-center">
                        提交成功！我们会尽快与您联系。
                      </p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-primary-600 text-center">{formError}</p>
                    )}
                  </form>
                </div>

                {/* FAQ + Info */}
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                    常见问题
                  </h2>
                  <div className="space-y-3 mb-8">
                    {faqs.map((faq, index) => (
                      <details
                        key={index}
                        className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                      >
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                          <span className="text-sm font-medium text-foreground-900">
                            {faq.q}
                          </span>
                          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                            <i className="ri-arrow-down-s-line"></i>
                          </span>
                        </summary>
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {faq.a}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>

                  <div className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-3">
                      联系方式
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">客服热线</p>
                          <p className="text-sm font-medium text-foreground-900">
                            400-888-0000
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">电子邮箱</p>
                          <p className="text-sm font-medium text-foreground-900">
                            service@baoyutv.com
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">公司地址</p>
                          <p className="text-sm font-medium text-foreground-900">
                            北京市朝阳区望京科技园
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                          <i className="ri-time-line"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">工作时间</p>
                          <p className="text-sm font-medium text-foreground-900">
                            周一至周日 9:00 - 22:00
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </main>
    </>
  );
}